import React from "react";
import { Plus } from "lucide-react";
import DragNumberInput from "../ui/DragNumberInput";
import InfoTooltip from "../ui/InfoTooltip";
import MenuSelect from "../ui/MenuSelect";
import PanelDeleteIconButton from "../draggable-panels/PanelDeleteIconButton";
import { TENDON_FIXED_MEMBER_GRID_CLASS } from "../../constants/ui";
import { canRemoveTendonMemberAt } from "../../utils/tendonMemberConstraints";
import PropertyPanelSection from "./PropertyPanelSection";
import { TENDON_COEFFICIENT_INFO } from "./tendonPanelConstants";

const FixedTendonMembersSection = ({
  tendon,
  hasUnusedJointOption,
  getJointOptionsForMember,
  onAddMember,
  onUpdateMemberJoint,
  onUpdateMemberCoef,
  onRemoveMember,
}) => (
  <PropertyPanelSection
    title="Member Links"
    actions={
      <button
        type="button"
        onClick={onAddMember}
        disabled={!hasUnusedJointOption}
        className="flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-gray-600 hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-40 text-gray-300 hover:text-gray-100"
      >
        <Plus className="w-3 h-3" />
        Add
      </button>
    }
    actionsClassName="gap-1"
  >
    <div
      className={`${TENDON_FIXED_MEMBER_GRID_CLASS} px-1 text-xs uppercase tracking-wider panel-text-helper`}
    >
      <div>Joint Name</div>
      <div className="flex items-center gap-1">
        <span>Coefficient</span>
        <InfoTooltip text={TENDON_COEFFICIENT_INFO} />
      </div>
      <div />
    </div>
    <div className="space-y-1">
      {(tendon.joints || []).length === 0 ? (
        <div className="panel-text-helper text-xs border border-gray-700 rounded p-2">
          No members
        </div>
      ) : (
        (tendon.joints || []).map((jRef, index) => {
          const canRemoveThisMember = canRemoveTendonMemberAt(tendon.joints, index);
          return (
            <div
              key={`${tendon.uuid}-member-${index}`}
              className="group flex items-center gap-2 px-1 py-1 text-xs"
            >
              <MenuSelect
                size="compact"
                value={jRef?.joint || ""}
                onChange={(value) => onUpdateMemberJoint(index, value)}
                className="flex-1 min-w-0"
                triggerClassName="min-w-0"
                options={getJointOptionsForMember(index)}
                contentClassName="max-h-[min(240px,50vh)]"
                triggerProps={{ onPointerDown: (event) => event.stopPropagation() }}
                aria-label="Tendon member joint"
              />
              <div className="w-20 shrink-0">
                <DragNumberInput
                  label="Coefficient"
                  value={Number(jRef?.coef ?? 1)}
                  dragValue={Number(jRef?.coef ?? 1)}
                  onChange={(event) => onUpdateMemberCoef(index, event.target.value)}
                  onDragValueChange={(nextValue) => onUpdateMemberCoef(index, nextValue)}
                  wrapperClassName="space-y-0"
                  labelClassName="sr-only"
                  inputClassName="w-20 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-xs"
                  inputProps={{ step: "0.01", "aria-label": "Coefficient" }}
                />
              </div>
              <PanelDeleteIconButton
                onClick={() => onRemoveMember(index)}
                disabled={!canRemoveThisMember}
                ariaLabel="Remove member"
                title={
                  canRemoveThisMember ? "Remove member" : "At least two distinct joints required"
                }
                revealOnParentHover
              />
            </div>
          );
        })
      )}
    </div>
  </PropertyPanelSection>
);

export default FixedTendonMembersSection;
